import React, { FunctionComponent } from 'react';

import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled from 'styled-components';

import moment from 'moment';

import SimpleArtist from '@common/types/artist/simple-artist';

import { MerchProduct } from '@services/merch/types/api/merch-product';
import { getMerchProductMask } from '@webapp/utils/merch/getMerchProductMask';

import Flex from '@components/layout/flex';
import Grid from '@components/basics/grid';
import SquareImg from '@components/basics/artwork-img';
import Typography from '@components/basics/typography';
import StatusLabelMerch from '@components/status-label-merch';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';
import MerchColorChoice from '@components/merch-row/merch-color-choice';
import { getMerchMenuOptions } from '@components/merch-row/menu-options';

import MenuDropdown from '@webapp/controllers/form/menu-dropdown';
import Pill from '@components/basics/pill';
import { SimpleRelease } from '@common/types/release/simple-release';

declare type MerchDesktopInfoRowProps = {
  className?: string;
  user: any;
  allReleases: SimpleRelease[];
  item: MerchProduct;
  artist: SimpleArtist;
  isListOpen: boolean;
  openList: () => void;
  baseUrl: string;
  handleShowNotification: () => void;
  handleCatalogMenu: (
    action: string,
    release: MerchProduct,
    baseUrl: string,
    artist: SimpleArtist,
    user: any,
    allReleases: SimpleRelease[],
    handleShowNotification: () => void
  ) => void;
};

const MerchDesktopInfoRow: FunctionComponent<MerchDesktopInfoRowProps> = ({
  className,
  user,
  allReleases,
  item,
  artist,
  isListOpen,
  openList,
  baseUrl,
  handleShowNotification,
  handleCatalogMenu,
}) => {
  const maskProduct = getMerchProductMask(item);
  const menuOptions = getMerchMenuOptions(item, artist);

  // TODO: DEVTEAM-603 - const timeLeft - This needs to be integrated
  const [timeLeft]: any = React.useState({
    days: 0,
    hours: 1,
    minutes: 26,
    seconds: 42,
    width: 50,
  });
  const inMinutes =
    timeLeft.days * 24 * 60 + timeLeft.hours * 60 + timeLeft.minutes;

  return (
    <Row className={`${className || ''} ${isListOpen ? 'menu-opened' : ''}`}>
      <Flex
        className="merch"
        justifyContent="space-between"
        alignItems="center"
        gap={20}
      >
        <Flex justifyContent="flex-start" alignItems="center" gap={20}>
          {!!maskProduct && (
            <SquareImg size={36} srcImg={maskProduct} altImg={item.title} />
          )}
          <Grid gap={5}>
            <Typography className="merch-name" customFont="semi-bold">
              {item.title}
            </Typography>
            <MerchColorChoice colors={item.colors} />
          </Grid>
        </Flex>
        {/*TODO: DEVTEAM-603 - Implement NFT for each merch. */}
        {item.isNFT && <Pill>NFT</Pill>}
      </Flex>

      <div className="date-added">
        <Typography type="small">
          {moment(item.createdAt).format('DD MMM YY')}
        </Typography>
      </div>

      <Grid className="price-profit" gap={2}>
        <Typography type="small">${item.price?.toFixed(2)}</Typography>
        <Typography type="small">
          ${Math.round(item.price - item.cost)?.toFixed(2)}
        </Typography>
      </Grid>

      <div className="status">
        {item.status !== 'publishing' ? (
          <StatusLabelMerch
            status={item.status}
            approved={item.status === 'published'}
            releaseDate={item.releaseAt}
          />
        ) : (
          <PublishingStatus progressWidth={100 - timeLeft?.width ?? 0}>
            <Typography type="small" weight="700" className="publishing-text">
              <Translate
                id="CATALOG.STATUS.PUBLISHING_IN"
                data={{
                  time: `${inMinutes >= 10 ? inMinutes : '0' + inMinutes}:${
                    timeLeft.seconds >= 10
                      ? timeLeft.seconds
                      : '0' + timeLeft.seconds
                  }`,
                }}
              />
            </Typography>
          </PublishingStatus>
        )}
      </div>

      <Flex
        className="actions"
        alignItems="center"
        justifyContent="flex-end"
        gap={5}
      >
        <StyledCircleArrowIcon
          isWhite
          smallArrow
          arrowUp={isListOpen}
          onClick={openList}
        />
        {menuOptions.length ? (
          <MenuButton>
            <MenuDropdown
              hasDottedIcon
              menuContent={menuOptions}
              onSelect={(action) =>
                handleCatalogMenu(
                  action,
                  item,
                  baseUrl,
                  artist,
                  user,
                  allReleases,
                  handleShowNotification
                )
              }
            />
          </MenuButton>
        ) : null}
      </Flex>
    </Row>
  );
};

export default MerchDesktopInfoRow;

const Row = styled.div`
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  width: 100%;
  display: grid;
  grid-template-columns: repeat(18, minmax(0, 1fr));
  grid-gap: 30px;
  align-items: center;
  padding: 30px;

  &.menu-opened {
    border-radius: 10px 10px 0 0;
  }

  .merch {
    grid-column: 1 / 7;
  }
  .date-added {
    grid-column: 7 / 9;
  }
  .price-profit {
    grid-column: 9 / 11;
  }
  .status {
    grid-column: 11 / 17;
  }
  .actions {
    grid-column: 17 / 19;
  }

  @media ${({ theme }) => theme.device.laptop} {
    .merch {
      grid-column: 1 / 9;
    }
    .date-added {
      grid-column: 9 / 11;
    }
    .price-profit {
      grid-column: 11 / 13;
    }
    .status {
      grid-column: 13 / 17;
    }
    .actions {
      grid-column: 17 / 19;
    }
  }

  .merch-name {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const MenuButton = styled.button`
  background-color: ${({ theme }) => theme.colors.differentBackground};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryHover};
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;

const StyledCircleArrowIcon = styled(CircleArrowIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

export const PublishingStatus = styled.div<{ progressWidth: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: relative;
  padding: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 100px;
  width: fit-content;
  max-width: 100%;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: calc(100% - ${({ progressWidth }) => progressWidth + '%'});
    bottom: 0;
    left: 0;
    border-radius: 100px;
    background-color: ${({ theme }) => rgba(theme.colors.upload, 0.1)};
    transition: right
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  .publishing-text {
    color: ${({ theme }) => theme.colors.upload};
  }
`;
